import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SubscriptionService } from '@core/subscription/subscription.service';
import { SubscriptionAddon, Subscription } from '@core/models/subscription.model';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent, DialogData, DialogResult } from '@shared/dialog/dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, timer } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedFormGroup } from '@feature/forms/form-model';
import { StripeService } from '@core/stripe/stripe.service';
import { AppStoreQuery } from '@core/state/app-state.query';
import { User } from '@core/models/user.model';

@UntilDestroy()
@Component({
  selector: 'wai-gp-addon-selector',
  templateUrl: './gp-addon-selector.component.html',
  styleUrls: ['./gp-addon-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GpAddonSelectorComponent implements OnInit {
  private static readonly ACTIVATION_POLL_INTERVAL_MS = 5000;
  private static readonly ACTIVATION_POLL_MAX_ATTEMPTS = 36;

  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() currentSubscription: Subscription | null = null;
  @Input() showPricing: boolean = true;
  @Input() allowMultiple: boolean = true;
  @Input() disablePurchase: boolean = false;
  @Output() subscriptionUpdated = new EventEmitter<Subscription>();

  public availableAddons$: Observable<SubscriptionAddon[]>;
  public isLoading: boolean = false;
  private isLoadingAddonUid: string | null = null;
  public pendingAddonForSepa: SubscriptionAddon | null = null;
  public pendingSepaPaymentMethodId: string | null = null;
  public billingContactName = '';
  public billingContactEmail = '';
  /** Add-ons subscribed via Stripe but not yet in activeAddons (awaiting SEPA / webhook). */
  private readonly pendingActivationAddonUids = new Set<string>();

  constructor(
    private subscriptionService: SubscriptionService,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private stripeService: StripeService,
    private appStoreQuery: AppStoreQuery,
  ) {}

  ngOnInit(): void {
    const currentUser = this.appStoreQuery.getValue()?.user as User | undefined;
    if (currentUser) {
      this.billingContactName = currentUser.getFullName() || '';
      this.billingContactEmail = currentUser.email || '';
    }
    this.loadAddons();
  }

  private loadAddons(): void {
    this.availableAddons$ = this.subscriptionService.getAvailableAddons().pipe(
      catchError(() => {
        this.snackBarService.error('INTL_ERROR_LOADING_ADDONS');
        return of([]);
      }),
    );
  }

  public isAddonLoading(addonUid: string): boolean {
    return this.isLoading && this.isLoadingAddonUid === addonUid;
  }

  public isAddonActive(addonUid: string): boolean {
    return this.currentSubscription?.activeAddons?.some((addon) => addon.uid === addonUid) || false;
  }

  public isAddonPendingActivation(addonUid: string): boolean {
    return this.pendingActivationAddonUids.has(addonUid);
  }

  public hasPendingAddonActivations(): boolean {
    return this.pendingActivationAddonUids.size > 0;
  }

  public onPurchaseAddon(addon: SubscriptionAddon): void {
    if (
      !addon?.uid ||
      this.isAddonActive(addon.uid) ||
      this.isAddonPendingActivation(addon.uid) ||
      this.disablePurchase
    ) {
      return;
    }

    this.pendingAddonForSepa = addon;
    this.pendingSepaPaymentMethodId = null;
    this.isLoadingAddonUid = addon.uid;
    this.cdr.markForCheck();
  }

  public onSepaSetupCompleted(paymentMethodId: string): void {
    if (!this.pendingAddonForSepa) {
      return;
    }
    this.pendingSepaPaymentMethodId = paymentMethodId;
    this.purchaseAddonWithStripe(this.pendingAddonForSepa, paymentMethodId);
  }

  public onSepaSetupCancelled(): void {
    this.pendingAddonForSepa = null;
    this.pendingSepaPaymentMethodId = null;
    this.isLoadingAddonUid = null;
    this.cdr.markForCheck();
  }

  private purchaseAddonWithStripe(addon: SubscriptionAddon, paymentMethodId: string): void {
    this.isLoading = true;
    this.isLoadingAddonUid = addon.uid;
    this.cdr.markForCheck();

    this.stripeService
      .subscribeGpAddon(addon.code, paymentMethodId)
      .pipe(
        untilDestroyed(this),
        switchMap((stripeResponse) =>
          this.subscriptionService.getCurrentSubscription(true).pipe(
            map((refreshedSubscription) => ({ stripeResponse, refreshedSubscription })),
          ),
        ),
        tap(({ stripeResponse, refreshedSubscription }) => {
          this.applySubscriptionUpdate(refreshedSubscription);
          this.pendingAddonForSepa = null;

          if (this.isAddonActive(addon.uid)) {
            this.pendingActivationAddonUids.delete(addon.uid);
            this.snackBarService.success('INTL_ADDON_PURCHASED_SUCCESSFULLY');
            return;
          }

          this.pendingActivationAddonUids.add(addon.uid);
          const messageKey = this.isStripePaymentSettled(stripeResponse.paymentStatus)
            ? 'INTL_ADDON_ACTIVATION_PENDING'
            : 'INTL_ADDON_PAYMENT_PROCESSING';
          this.snackBarService.open(this.translate.instant(messageKey), 'info');
          this.startAddonActivationPolling(addon.uid);
        }),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingAddonUid = null;
          this.pendingSepaPaymentMethodId = null;
          this.cdr.markForCheck();
        }),
        catchError((error) => {
          const errorMessage =
            error?.error?.error ||
            error?.error?.detail ||
            error?.error?.message ||
            'INTL_ERROR_PURCHASING_ADDON';
          this.snackBarService.error(errorMessage);
          return of(null);
        }),
      )
      .subscribe();
  }

  private applySubscriptionUpdate(subscription: Subscription): void {
    if (this.currentSubscription) {
      this.currentSubscription = subscription;
    }
    this.subscriptionUpdated.emit(subscription);
    this.cdr.markForCheck();
  }

  private isStripePaymentSettled(paymentStatus: string): boolean {
    return (paymentStatus || '').toLowerCase() === 'active';
  }

  private startAddonActivationPolling(addonUid: string): void {
    timer(
      GpAddonSelectorComponent.ACTIVATION_POLL_INTERVAL_MS,
      GpAddonSelectorComponent.ACTIVATION_POLL_INTERVAL_MS,
    )
      .pipe(
        take(GpAddonSelectorComponent.ACTIVATION_POLL_MAX_ATTEMPTS),
        switchMap(() => this.subscriptionService.getCurrentSubscription(true)),
        tap((refreshedSubscription) => this.applySubscriptionUpdate(refreshedSubscription)),
        filter((refreshedSubscription) => this.isAddonActiveOnSubscription(refreshedSubscription, addonUid)),
        take(1),
        untilDestroyed(this),
        finalize(() => {
          if (!this.pendingActivationAddonUids.has(addonUid)) {
            return;
          }
          this.pendingActivationAddonUids.delete(addonUid);
          this.snackBarService.open(this.translate.instant('INTL_ADDON_ACTIVATION_STILL_PENDING'), 'warn');
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.pendingActivationAddonUids.delete(addonUid);
          this.snackBarService.success('INTL_ADDON_PURCHASED_SUCCESSFULLY');
          this.cdr.markForCheck();
        },
      });
  }

  private isAddonActiveOnSubscription(subscription: Subscription, addonUid: string): boolean {
    return subscription?.activeAddons?.some((addon) => addon.uid === addonUid) || false;
  }

  public onRemoveAddon(addonUid: string): void {
    if (!addonUid || !this.isAddonActive(addonUid)) {
      return;
    }

    // Get addon name/type for confirmation message
    this.availableAddons$
      .pipe(
        take(1),
        switchMap((addons) => {
          const addon = addons.find((a) => a.uid === addonUid);
          const addonName = addon?.name || this.currentSubscription?.activeAddons?.find((a) => a.uid === addonUid)?.name || 'this add-on';
          const addonType = addon?.addonType || this.currentSubscription?.activeAddons?.find((a) => a.uid === addonUid)?.addonType || '';

          // Show confirmation dialog
          const addonTypeText = addonType ? ` (${addonType})` : '';
          const confirmMessage = this.translate.instant('INTL_CONFIRM_DELETE_ADDON')
            .replace('{{addonName}}', addonName)
            .replace('{{addonType}}', addonTypeText);
          const confirmDialogData: DialogData = {
            title: this.translate.instant('INTL_CONFIRM_DELETE'),
            text: confirmMessage,
            confirmButton: {
              label: this.translate.instant('INTL_YES'),
              color: 'warn',
              icon: 'delete',
            },
            dismissButton: {
              label: this.translate.instant('INTL_CANCEL'),
              icon: 'close',
            },
          };

          const confirmDialogRef = this.dialog.open<DialogComponent, DialogData, DialogResult>(DialogComponent, {
            data: confirmDialogData,
            width: '500px',
            disableClose: true,
          });

          return confirmDialogRef.afterClosed();
        }),
        switchMap((confirmed) => {
          if (!confirmed) {
            // User cancelled, return early
            return of(null);
          }

          // User confirmed, proceed with deletion
          this.isLoading = true;
          this.isLoadingAddonUid = addonUid;
          this.cdr.markForCheck();

          return this.subscriptionService.removeAddon(addonUid).pipe(
            switchMap(() => {
              // Refresh subscription to get latest data including activeAddons
              return this.subscriptionService.getCurrentSubscription(true).pipe(
                tap((refreshedSubscription) => {
                  // Update current subscription if input is bound
                  if (this.currentSubscription) {
                    this.currentSubscription = refreshedSubscription;
                  }
                  // Emit event to notify parent component (profile information) to refresh
                  this.subscriptionUpdated.emit(refreshedSubscription);
                  this.snackBarService.success('INTL_ADDON_REMOVED_SUCCESSFULLY');
                  this.cdr.markForCheck();
                }),
              );
            }),
            finalize(() => {
              this.isLoading = false;
              this.isLoadingAddonUid = null;
              this.cdr.markForCheck();
            }),
            catchError((error) => {
              const errorMessage =
                error?.error?.error ||
                error?.error?.detail ||
                error?.error?.message ||
                'INTL_ERROR_REMOVING_ADDON';
              this.snackBarService.error(errorMessage);
              return of(null);
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public getAddonIcon(addonType: string): string {
    const icons: Record<string, string> = {
      featured_fund: 'star',
      due_diligence: 'description',
      placement_agent: 'business',
    };
    return icons[addonType] || 'add_circle';
  }

  /**
   * Get the color class for addon based on addonType
   * Matches GP badge colors: Purple for Featured Fund, Blue for Due Diligence, Orange for default
   */
  public getAddonColorClass(addonType: string): string {
    const colorMap: Record<string, string> = {
      featured_fund: 'addon-purple',
      due_diligence: 'addon-blue',
      placement_agent: 'addon-orange', // Default color for other addons
    };
    return colorMap[addonType] || 'addon-orange';
  }

  public getAddonFeatures(addonType: string): string[] {
    const features: Record<string, string[]> = {
      featured_fund: ['INTL_ENHANCED_MARKETPLACE_PROMINENCE', 'INTL_FEATURED_PLACEMENT'],
      due_diligence: ['INTL_DDQ_ACCESS', 'INTL_DUE_DILIGENCE_TOOLS'],
      placement_agent: ['INTL_PLACEMENT_AGENT_FEATURES', 'INTL_ADVANCED_TOOLS'],
    };
    return features[addonType] || [];
  }

  /**
   * Parse description field that may contain \r\n line breaks and return as array
   * Similar to subscription upgrade dialog component
   * @param description The description string that may contain \r\n
   * @returns Array of description items, or single-item array if no line breaks
   */
  public getDescriptionList(description: string | null | undefined): string[] {
    if (!description) {
      return [];
    }

    // Split by \r\n, \n, or \r and filter out empty strings
    const items = description
      .split(/\r\n|\r|\n/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    return items.length > 0 ? items : [description];
  }
}
